import { Link } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  Award,
  ShieldCheck,
  Users,
  Hammer,
  Clock,
  HeartHandshake,
  CheckCircle2,
} from "lucide-react";
import { BRAND } from "../../lib/brand.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { SEO } from "../../lib/seo.jsx";

// What drives every Shere-e-Punjab Homes build.
const VALUES = [
  {
    icon: Users,
    title: "One team, sketch to handover",
    body: "The same designer, project lead, and construction manager carry your build from the first conversation to the day we hand you the keys.",
  },
  {
    icon: ShieldCheck,
    title: "Fixed-price guarantee",
    body: "The number on the contract is the number you pay — no surprise variations, no hidden fees, no end-of-build shock.",
  },
  {
    icon: Hammer,
    title: "Built by our own trades",
    body: "We don't farm work out. Our supervisors are on every site and every stage is signed off in person.",
  },
  {
    icon: Clock,
    title: "Aftercare baked in",
    body: "6-month and 12-month aftercare reviews are part of every build — never an upsell, never an afterthought.",
  },
  {
    icon: HeartHandshake,
    title: "Honest, plain-English advice",
    body: "Plain-English specs, contracts, and variations — so you always know exactly what you're signing.",
  },
];

const STORY_HIGHLIGHTS = [
  "Over two decades of custom home building",
  "Founded and still led by builder Mandip Singh",
  "Every home drawn in-house — never off a catalogue",
  "Fixed-price contracts on every single build",
];

export default function CompanyProfilePage() {
  const brand = BRAND;
  const areas = brand.serviceAreas ?? [];

  return (
    <>
      <SEO
        title="Company Profile"
        description={`About ${brand.name ?? "Shere-e-Punjab Homes"} — a custom home builder with 20+ years of experience across Melbourne, Geelong, Ballarat and Bendigo.`}
      />

      <PageHero
        eyebrow="About us"
        title={brand.name ?? "Shere-e-Punjab Homes"}
        description="Two decades of bespoke custom homes — drawn in-house, built by our own teams, and backed by a fixed-price guarantee."
      >
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-accent">
            <Award className="h-3.5 w-3.5" />
            20+ years of experience
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-border-subtle bg-bg-base/60 px-4 py-1.5 text-xs text-text-secondary">
            <MapPin className="h-3.5 w-3.5 text-accent" />
            {areas.join(" · ")}
          </span>
        </div>
      </PageHero>

      {/* Story — narrative + image collage. */}
      <Container className="py-16 lg:py-24">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="eyebrow">Our story</p>
            <h2 className="mt-3 font-display text-3xl text-text-primary lg:text-4xl">
              Two decades of building homes the right way
            </h2>
            <div className="mt-6 space-y-4 leading-relaxed text-text-secondary">
              <p>
                Founded by builder Mandip Singh, Shere-e-Punjab Homes has spent
                more than 20 years pairing traditional craftsmanship with a fully
                custom-build pathway. What started as a single-builder operation
                is now a trusted name across four Victorian regions.
              </p>
              <p>
                Every home — from a 3-bedroom Plato to our flagship Royale 42 — is
                drawn in-house, built by our own teams, and backed by a
                fixed-price guarantee. Twenty years on, the promise hasn&apos;t
                changed: one team, one quote, one project lead, sketch to
                handover.
              </p>
            </div>

            <ul className="mt-8 space-y-3">
              {STORY_HIGHLIGHTS.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-text-primary">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <Link to="/about/why-build-with-us" className="btn-primary mt-9">
              Why build with us
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <img
                src="/gallery-1.jpg"
                alt="A completed Shere-e-Punjab home"
                className="h-56 w-full rounded-2xl border border-border-subtle object-cover"
              />
              <img
                src="/gallery-3.jpg"
                alt="Interior finishes on a custom build"
                className="h-40 w-full rounded-2xl border border-border-subtle object-cover"
              />
            </div>
            <div className="space-y-4 pt-8">
              <img
                src="/gallery-5.jpg"
                alt="A façade detail from a recent project"
                className="h-40 w-full rounded-2xl border border-border-subtle object-cover"
              />
              <div className="rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/20 via-bg-elevated to-bg-elevated p-6">
                <Award className="h-7 w-7 text-accent" />
                <p className="mt-3 font-display text-3xl text-text-primary">20+</p>
                <p className="mt-1 text-sm text-text-secondary">
                  years of trusted home building across Victoria
                </p>
              </div>
            </div>
          </div>
        </div>
      </Container>

      {/* Values — what drives every build. */}
      <section className="border-y border-border-subtle bg-bg-elevated py-16 lg:py-24">
        <Container>
          <div className="max-w-2xl">
            <p className="eyebrow">What we believe</p>
            <h2 className="mt-3 font-display text-3xl text-text-primary lg:text-4xl">
              The standards behind every Shere-e-Punjab home
            </h2>
            <p className="mt-4 text-text-secondary">
              Twenty years of building has taught us what matters. These are the
              commitments we hold to on every project, every time.
            </p>
          </div>

          <ul className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map(({ icon: Icon, title, body }) => (
              <li
                key={title}
                className="group relative overflow-hidden rounded-2xl border border-border-subtle bg-bg-base p-6 transition-all hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-card-hover"
              >
                <span
                  aria-hidden
                  className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-accent/0 transition-colors group-hover:bg-accent/10"
                />
                <span className="relative grid h-12 w-12 place-items-center rounded-xl bg-accent/15 text-accent">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="relative mt-5 font-display text-xl text-text-primary">{title}</h3>
                <p className="relative mt-2 text-sm text-text-secondary">{body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* Where we build — the four regions. */}
      <Container className="py-16 lg:py-24">
        <div className="max-w-2xl">
          <p className="eyebrow">Where we build</p>
          <h2 className="mt-3 font-display text-3xl text-text-primary lg:text-4xl">
            Proudly building across four Victorian regions
          </h2>
          <p className="mt-4 text-text-secondary">
            From metro Melbourne to the regional centres of Geelong, Ballarat and
            Bendigo — wherever your block is, our team comes to you.
          </p>
        </div>

        <ul className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {areas.map((area) => (
            <li
              key={area}
              className="group flex items-center gap-3 rounded-xl border border-border-subtle bg-bg-elevated p-5 transition-colors hover:border-accent/60"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-accent/15 text-accent">
                <MapPin className="h-5 w-5" />
              </span>
              <span className="font-display text-lg text-text-primary">{area}</span>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-sm text-text-muted">
          Don&apos;t see your suburb? Get in touch — we travel for the right project.
        </p>
      </Container>

      {/* Contact CTA. */}
      <section className="border-t border-border-subtle bg-bg-elevated py-16 lg:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="eyebrow">Talk to us</p>
              <h2 className="mt-3 font-display text-3xl text-text-primary lg:text-4xl">
                Let&apos;s talk about your build
              </h2>
              <p className="mt-4 text-text-secondary">
                Twenty years in, we still answer every enquiry ourselves. Send us
                a brief and we&apos;ll come prepared with sketches, specs, and an
                honest budget conversation.
              </p>
              <Link to="/contact-us" className="btn-primary mt-8">
                Get in touch
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="rounded-2xl border border-border-subtle bg-bg-base p-6 lg:p-8">
              <ul className="space-y-4 text-sm">
                {brand.address && (
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                    <span className="text-text-secondary">
                      {brand.address}
                      {brand.serviceAreaLine && (
                        <span className="mt-1 block font-medium text-accent">
                          {brand.serviceAreaLine}
                        </span>
                      )}
                    </span>
                  </li>
                )}
                {brand.phone && (
                  <li className="flex items-center gap-3">
                    <Phone className="h-5 w-5 shrink-0 text-accent" />
                    <a
                      href={`tel:${brand.phone.replace(/\s+/g, "")}`}
                      className="text-text-secondary hover:text-accent"
                    >
                      {brand.phone}
                    </a>
                  </li>
                )}
                {brand.email && (
                  <li className="flex items-center gap-3">
                    <Mail className="h-5 w-5 shrink-0 text-accent" />
                    <a
                      href={`mailto:${brand.email}`}
                      className="text-text-secondary hover:text-accent"
                    >
                      {brand.email}
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
